import { c as _c } from "react/compiler-runtime";
import { BedSolid, RoutePictogram, Sparkles, Ticket, TrainPictogram } from '@byway/icons';
import { format, parseISO } from 'date-fns';
import { useTranslations } from 'next-intl';
import { formatDurationHM } from 'utils/DateFormats';
import { Divider } from '~/components/Divider';
import { ChangeTravelTimesField } from '~/components/TripOverview/transportPage/ChangeTravelTimesField';
import { visuallyHidden } from '~/components/Utilities';
import { getTicketsIncludedKey, hasPrivateCabin } from '~/constants/transport-modes';
import type { TripSection } from '~/stores/tpv2/journeyStore';
import type { LegTransformed, Transport } from '~/types/JourneyItem.types';
import { ItineraryCard } from './ItineraryCard';
import type { TagItem } from './ItineraryCard.types';
import legStyles from './LegCard.module.css';
import { useSleeperTag } from './useSleeperTag';
type HeroRouteCardProps = LegTransformed & {
  section: TripSection;
};
function formatDate(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'd MMM yy');
  } catch {
    return dateStr;
  }
}
function formatStationLabel(city: string, terminalId: string | null): string {
  return terminalId ? `${city} (${terminalId.split('-')[1]?.toLowerCase() ?? terminalId})` : city;
}
export const HeroRouteCard = t0 => {
  const $ = _c(77);
  const {
    itemId,
    origin,
    destination,
    viaStops,
    startDate,
    departureTimeText,
    arrivalTimeText,
    duration,
    fromTerminalId,
    fromTerminalName,
    toTerminalId,
    toTerminalName,
    images,
    body,
    description,
    interrail,
    channel,
    modeOfTransports,
    section
  } = t0;
  const t = useTranslations("journey");
  const tAria = useTranslations("aria");
  let t1;
  if ($[0] !== viaStops) {
    t1 = viaStops ?? [];
    $[0] = viaStops;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const ticketsIncludedKey = getTicketsIncludedKey(modeOfTransports.filter(Boolean) as Transport[], t1.length > 0);
  let t2;
  if ($[2] !== t) {
    t2 = t("overview_hero_route_tag");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Sparkles size="small" decorative={true} />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2) {
    t4 = {
      label: t2,
      variant: "hero",
      icon: t3
    };
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const heroTag = t4;
  const sleeperTag = useSleeperTag(modeOfTransports);
  const galleryImages = images?.length ? images : null;
  const showHeroTag = section !== "transport";
  const galleryTag = showHeroTag ? heroTag : sleeperTag;
  const HeaderIcon = section === "overview" ? RoutePictogram : TrainPictogram;
  const tags = galleryImages || !showHeroTag ? [] : [heroTag];
  if (interrail || channel === "RAIL") {
    let t5;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Ticket size="small" decorative={true} />;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    tags.push({
      label: t(ticketsIncludedKey),
      variant: "positive",
      icon: t5
    });
  }
  const hasTravelTimes = Boolean(departureTimeText || arrivalTimeText);
  if (section === "transport" && !hasTravelTimes) {
    let t5;
    if ($[8] !== t) {
      t5 = t("transport_times_tbc_tag");
      $[8] = t;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== t5) {
      t6 = {
        label: t5
      };
      $[10] = t5;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    tags.push(t6);
  }
  let t5;
  if ($[12] !== modeOfTransports || $[13] !== section) {
    t5 = section === "transport" && hasPrivateCabin(modeOfTransports);
    $[12] = modeOfTransports;
    $[13] = section;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const showPrivateCompartment = t5;
  let t6;
  if ($[15] !== startDate) {
    t6 = formatDate(startDate);
    $[15] = startDate;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const formattedDate = t6;
  let t7;
  if ($[17] !== duration) {
    t7 = duration ? formatDurationHM({
      minutes: duration
    }) : "";
    $[17] = duration;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const formattedDuration = t7;
  let t8;
  if ($[19] !== fromTerminalId || $[20] !== fromTerminalName || $[21] !== origin) {
    t8 = fromTerminalName ?? formatStationLabel(origin, fromTerminalId);
    $[19] = fromTerminalId;
    $[20] = fromTerminalName;
    $[21] = origin;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const originStation = t8;
  let t9;
  if ($[23] !== destination || $[24] !== toTerminalId || $[25] !== toTerminalName) {
    t9 = toTerminalName ?? formatStationLabel(destination, toTerminalId);
    $[23] = destination;
    $[24] = toTerminalId;
    $[25] = toTerminalName;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const destinationStation = t9;
  const bodyText = body?.[0] && "text" in body[0] ? String(body[0].text) : description ?? "";
  let t10;
  if ($[27] !== HeaderIcon) {
    t10 = <HeaderIcon size="small" decorative={true} />;
    $[27] = HeaderIcon;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span aria-hidden="true"> → </span>;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== tAria) {
    t12 = tAria("to");
    $[30] = tAria;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const t13 = ` ${t12} `;
  let t14;
  if ($[32] !== t13) {
    t14 = <span className={visuallyHidden}>{t13}</span>;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== destination || $[35] !== origin || $[36] !== t14) {
    t15 = <>{origin}{t11}{t14}{destination}</>;
    $[34] = destination;
    $[35] = origin;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t10 || $[39] !== t15) {
    t16 = <ItineraryCard.Header icon={t10} title={t15} />;
    $[38] = t10;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== galleryImages || $[42] !== galleryTag) {
    t17 = galleryImages && <ItineraryCard.Gallery images={galleryImages} tag={galleryTag ? <ItineraryCard.Tags tags={[galleryTag]} /> : undefined} />;
    $[41] = galleryImages;
    $[42] = galleryTag;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== tags) {
    t18 = <ItineraryCard.Tags tags={tags} />;
    $[44] = tags;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== showPrivateCompartment || $[47] !== t) {
    t19 = showPrivateCompartment && <div className={legStyles.detailRow}><BedSolid size="small" decorative={true} /><span>{t("transport_private_compartment_tag")}</span></div>;
    $[46] = showPrivateCompartment;
    $[47] = t;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== section || $[50] !== t) {
    t20 = section === "overview" && <ItineraryCard.InfoBanner>{t("overview_choose_times")}</ItineraryCard.InfoBanner>;
    $[49] = section;
    $[50] = t;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== arrivalTimeText || $[53] !== bodyText || $[54] !== channel || $[55] !== departureTimeText || $[56] !== destination || $[57] !== destinationStation || $[58] !== formattedDate || $[59] !== formattedDuration || $[60] !== hasTravelTimes || $[61] !== itemId || $[62] !== origin || $[63] !== originStation || $[64] !== section || $[65] !== startDate) {
    t21 = section === "transport" && hasTravelTimes && <ItineraryCard.Content><ItineraryCard.TrainTimes date={formattedDate} departureTime={departureTimeText} arrivalTime={arrivalTimeText} originStation={originStation} destinationStation={destinationStation} duration={formattedDuration} /><ChangeTravelTimesField itemId={itemId} startDate={startDate} origin={origin} destination={destination} departureTimeText={departureTimeText} arrivalTimeText={arrivalTimeText} isEurostar={channel === "EUROSTAR"} />{bodyText && <Divider variant="horizontal" />}</ItineraryCard.Content>;
    $[52] = arrivalTimeText;
    $[53] = bodyText;
    $[54] = channel;
    $[55] = departureTimeText;
    $[56] = destination;
    $[57] = destinationStation;
    $[58] = formattedDate;
    $[59] = formattedDuration;
    $[60] = hasTravelTimes;
    $[61] = itemId;
    $[62] = origin;
    $[63] = originStation;
    $[64] = section;
    $[65] = startDate;
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  let t22;
  if ($[67] !== bodyText) {
    t22 = bodyText && <ItineraryCard.Body text={bodyText} />;
    $[67] = bodyText;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  let t23;
  if ($[69] !== t16 || $[70] !== t17 || $[71] !== t18 || $[72] !== t19 || $[73] !== t20 || $[74] !== t21 || $[75] !== t22) {
    t23 = <ItineraryCard>{t16}{t17}{t18}{t19}{t20}{t21}{t22}</ItineraryCard>;
    $[69] = t16;
    $[70] = t17;
    $[71] = t18;
    $[72] = t19;
    $[73] = t20;
    $[74] = t21;
    $[75] = t22;
    $[76] = t23;
  } else {
    t23 = $[76];
  }
  return t23;
};